import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { chatService } from '../../services/chat.service';
import {
  ChatState,
  Conversation,
  Message,
  CreateConversationRequest,
  MessagesListParams,
  UserStatus,
} from '../../types/chat.types';

// Helper function for safe sorting
const sortConversations = (conversations: Conversation[]): Conversation[] => {
  return [...conversations].sort((a, b) => {
    const dateA = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
    const dateB = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
    return dateB - dateA;
  });
};

// Розширюємо інтерфейс ChatState
interface ExtendedChatState extends ChatState {
  typingUsers: Record<number, string[]>; // conversationId -> [userNames]
}

const initialState: ExtendedChatState = {
  conversations: [],
  activeConversationId: null,
  messages: {},
  loadingConversations: false,
  loadingMessages: false,
  sendingMessage: false,
  error: null,
  messagesPage: {},
  hasMoreMessages: {},
  isConnected: false,
  totalUnreadCount: 0,
  typingUsers: {}, // Ініціалізація
};

export const fetchConversations = createAsyncThunk(
  'chat/fetchConversations',
  async (_, { rejectWithValue }) => {
    try {
      const conversations = await chatService.getConversations();
      return sortConversations(conversations);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Помилка завантаження розмов');
    }
  },
);

export const handleNewMessage = createAsyncThunk(
  'chat/handleNewMessage',
  async (message: Message, { dispatch }) => {
    // console.log('🚀 [DEBUG] Thunk handleNewMessage called with:', message);
    dispatch(addMessage(message));
  },
);

export const createConversation = createAsyncThunk(
  'chat/createConversation',
  async (data: CreateConversationRequest, { rejectWithValue }) => {
    try {
      const response = await chatService.createConversation(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Помилка створення розмови');
    }
  },
);

export const fetchMessages = createAsyncThunk(
  'chat/fetchMessages',
  async (
    { conversationId, params = {} }: { conversationId: number; params?: MessagesListParams },
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as { chat: ExtendedChatState };
      const currentPage = state.chat.messagesPage[conversationId] || 1;

      const messages = await chatService.getMessages(conversationId, {
        ...params,
        page: params.page || currentPage,
      });

      return {
        conversationId,
        messages: messages.reverse(),
        page: params.page || currentPage,
        isLoadMore: (params.page || currentPage) > 1,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Помилка завантаження повідомлень');
    }
  },
);

export const markAllAsRead = createAsyncThunk(
  'chat/markAllAsRead',
  async (conversationId: number, { rejectWithValue }) => {
    try {
      await chatService.markAllAsRead(conversationId);
      return conversationId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Не вдалося позначити розмову як прочитану');
    }
  },
);

export const markMessageAsRead = createAsyncThunk(
  'chat/markMessageAsRead',
  async (messageId: string, { rejectWithValue }) => {
    try {
      await chatService.markMessageAsRead(messageId);
      return messageId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Не вдалося позначити повідомлення як прочитане');
    }
  },
);

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    updateUserStatus: (state, action: PayloadAction<{ userId: number; status: UserStatus }>) => {
      const { userId, status } = action.payload;
      state.conversations.forEach(conv => {
        if (conv.participantId === userId) {
          conv.participantStatus = status;
        }
      });
    },
    setTotalUnreadCount: (state, action: PayloadAction<number>) => {
      // console.log('🔢 [DEBUG] Reducer setTotalUnreadCount called. New value:', action.payload);
      state.totalUnreadCount = action.payload;
    },
    setSocketConnected: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload;
    },
    setActiveConversation: (state, action: PayloadAction<number | null>) => {
      state.activeConversationId = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    addMessage: (state, action: PayloadAction<Message>) => {
      const message = action.payload;
      const { conversationId } = message;

      // console.log(`📥 [DEBUG] Reducer addMessage called for conversationId: ${conversationId}. Message ID: ${message.id}, isRead: ${message.isRead}`);

      // 1. Add message to the messages list
      if (!state.messages[conversationId]) {
        state.messages[conversationId] = [];
      }
      const exists = state.messages[conversationId].some((m) => m.id === message.id);
      if (!exists) {
        state.messages[conversationId].push(message);
      } else {
        // console.log('⚠️ [DEBUG] Message already exists in state.messages');
      }

      // 2. Update the conversation in the list
      const conversationIndex = state.conversations.findIndex(c => c.id === conversationId);
      // console.log(`🔍 [DEBUG] Conversation found at index: ${conversationIndex}`);
      
      if (conversationIndex !== -1) {
        const conversation = state.conversations[conversationIndex];
        
        // Update preview and time
        conversation.lastMessage = message.message;
        conversation.lastMessageAt = message.sentAt;

        // Increment unread count
        if (!message.isRead) {
           const currentCount = typeof conversation.unreadCount === 'number' ? conversation.unreadCount : 0;
           // console.log(`📈 [DEBUG] Incrementing unread count. Old: ${currentCount}, New: ${currentCount + 1}`);
           
           conversation.unreadCount = currentCount + 1;
           state.totalUnreadCount += 1;
           // console.log(`📈 [DEBUG] Total unread count updated to: ${state.totalUnreadCount}`);
        } else {
           // console.log('ℹ️ [DEBUG] Message is marked as read, skipping increment.');
        }

        // Move conversation to top
        state.conversations.splice(conversationIndex, 1);
        state.conversations.unshift(conversation);
      } else {
        // console.warn('⚠️ [DEBUG] Conversation not found in list! Cannot update unread count.');
      }
    },
    addConversation: (state, action: PayloadAction<Conversation>) => {
      const newConversation = action.payload;
      const exists = state.conversations.some(c => c.id === newConversation.id);
      if (!exists) {
        state.conversations.push(newConversation);
        state.conversations = sortConversations(state.conversations);
        
        const count = typeof newConversation.unreadCount === 'string' ? parseInt(newConversation.unreadCount) : newConversation.unreadCount;
        const validCount = isNaN(count) ? 0 : count;
        state.totalUnreadCount += validCount;
      }
    },
    updateMessageAsRead: (state, action: PayloadAction<string>) => {
      const messageId = action.payload;
      Object.values(state.messages).forEach(messages => {
        const message = messages.find(m => m.id === messageId);
        if (message) {
          message.isRead = true;
        }
      });
    },
    // --- New Action for Real-time Read Status ---
    messageRead: (state, action: PayloadAction<{ messageId: string; conversationId: number }>) => {
      const { messageId, conversationId } = action.payload;
      console.log(`👁️ [Reducer] messageRead: msg ${messageId} in chat ${conversationId}`);
      
      if (state.messages[conversationId]) {
        const message = state.messages[conversationId].find(m => m.id === messageId);
        if (message) {
          message.isRead = true;
        }
      }
    },
    // --- Typing Actions ---
    userStartedTyping: (state, action: PayloadAction<{ conversationId: number; userName: string }>) => {
      const { conversationId, userName } = action.payload;
      console.log(`⌨️ [Reducer] userStartedTyping: ${userName} in chat ${conversationId}`);
      if (!state.typingUsers[conversationId]) {
        state.typingUsers[conversationId] = [];
      }
      if (!state.typingUsers[conversationId].includes(userName)) {
        state.typingUsers[conversationId].push(userName);
      }
    },
    userStoppedTyping: (state, action: PayloadAction<{ conversationId: number; userName: string }>) => {
      const { conversationId, userName } = action.payload;
      console.log(`🛑 [Reducer] userStoppedTyping: ${userName} in chat ${conversationId}`);
      if (state.typingUsers[conversationId]) {
        const initialLength = state.typingUsers[conversationId].length;
        state.typingUsers[conversationId] = state.typingUsers[conversationId].filter(name => name !== userName);
        const finalLength = state.typingUsers[conversationId].length;
        console.log(`   Removed? ${initialLength > finalLength}. Remaining: ${state.typingUsers[conversationId]}`);
      } else {
        console.log(`   No typing users found for chat ${conversationId}`);
      }
    },
    resetChat: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConversations.pending, (state) => {
        state.loadingConversations = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action: PayloadAction<Conversation[]>) => {
        state.loadingConversations = false;

        const uniqueConversationsMap = new Map<number, Conversation>();
        action.payload.forEach(conv => {
          uniqueConversationsMap.set(conv.id, conv);
        });
        const uniqueConversations = Array.from(uniqueConversationsMap.values());

        state.conversations = uniqueConversations.map(conv => ({
          ...conv,
          unreadCount: conv.unreadCount || 0,
        }));

        state.totalUnreadCount = state.conversations.reduce((total, conv) => {
             const count = typeof conv.unreadCount === 'string' ? parseInt(conv.unreadCount) : conv.unreadCount;
             return total + (isNaN(count) ? 0 : count);
        }, 0);
        
        // console.log('📚 [DEBUG] Conversations fetched. Total unread:', state.totalUnreadCount);

        state.error = null;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loadingConversations = false;
        state.error = action.payload as string;
      })
      .addCase(createConversation.fulfilled, (state, action) => {
        const { id } = action.payload;
        state.activeConversationId = id;
      })
      .addCase(fetchMessages.pending, (state) => {
        state.loadingMessages = true;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        const { conversationId, messages, page, isLoadMore } = action.payload;
        state.loadingMessages = false;
        if (isLoadMore) {
          const existing = state.messages[conversationId] || [];
          state.messages[conversationId] = [...messages, ...existing];
        } else {
          state.messages[conversationId] = messages;
        }
        state.messagesPage[conversationId] = page;
        state.hasMoreMessages[conversationId] = messages.length === 50;
      })
      .addCase(fetchMessages.rejected, (state) => {
        state.loadingMessages = false;
      })
      .addCase(markAllAsRead.fulfilled, (state, action: PayloadAction<number>) => {
        const conversationId = action.payload;
        const conversation = state.conversations.find(c => c.id === conversationId);
        if (conversation) {
          const oldCount = typeof conversation.unreadCount === 'string' ? parseInt(conversation.unreadCount) : conversation.unreadCount;
          const validOldCount = isNaN(oldCount) ? 0 : oldCount;

          conversation.unreadCount = 0;
          state.totalUnreadCount = Math.max(0, state.totalUnreadCount - validOldCount);
        }
        if (state.messages[conversationId]) {
          state.messages[conversationId].forEach(m => {
            if (!m.isRead) {
              m.isRead = true;
            }
          });
        }
      })
      .addCase(markMessageAsRead.fulfilled, (state, action: PayloadAction<string>) => {
        const messageId = action.payload;
        Object.values(state.messages).forEach(messages => {
          const message = messages.find(m => m.id === messageId);
          if (message && !message.isRead) {
            message.isRead = true;
            const conversation = state.conversations.find(c => c.id === message.conversationId);
            if (conversation && conversation.unreadCount > 0) {
              conversation.unreadCount -= 1;
              state.totalUnreadCount = Math.max(0, state.totalUnreadCount - 1);
            }
          }
        });
      });
  },
});

export const {
  updateUserStatus,
  setTotalUnreadCount,
  setSocketConnected,
  setActiveConversation,
  clearError,
  addMessage,
  addConversation,
  updateMessageAsRead,
  userStartedTyping,
  userStoppedTyping,
  messageRead, // Export new action
  resetChat,
} = chatSlice.actions;

export default chatSlice.reducer;